import React, { useState, useEffect, useContext } from 'react';
import classnames from 'classnames';
import { withStyles } from '@material-ui/styles';
import { DynamicForm } from '../../components/DynamicForm';
import * as v from '../../helpers/validation';
import PropTypes from 'prop-types';
import { AppContext } from '../../AppContext';
import { Typography, Chip } from '@material-ui/core';

// SERVICES
import { updatePassword, updateMyAccount, getUserById } from '../../services';
import { getTrustedDevices } from '../../services/mfaManagement.service';

const styles = theme => ({});

export const MyAccount = withStyles(styles)(props => {
  const {
    state: { userId, username, email, phoneNumber },
  } = useContext(AppContext);
  const [accountInfo, setAccountInfo] = useState({
    id: userId,
    userName: username,
    email: email || '', 
    phoneNumber: phoneNumber || '',
    password: '',
    confirmPassword: '',
  });
  const [mfaInfo, setMfaInfo] = useState(null);
  const [trustedDevices, setTrustedDevices] = useState([]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const user = await getUserById(userId);
        
        setMfaInfo({
          twoFactorEnabled: user.twoFactorEnabled || user.TwoFactorEnabled,
        });
        
        setAccountInfo(prev => ({
          ...prev,
          email: user.email || user.Email || prev.email,
          phoneNumber: user.phoneNumber || user.PhoneNumber || '',
        }));

        // Fetch trusted devices
        try {
          const devices = await getTrustedDevices(userId);
          setTrustedDevices(devices || []);
        } catch (err) {
          // Silently fail if no devices
          setTrustedDevices([]);
        }
      } catch (err) {
        //
      }
    };
    if (userId) {
      fetchUserInfo();
    }
  }, [userId]);


  const handleSave = async (formValues) => {
    const updates = [];
    const hasPasswordChange = formValues.password && formValues.confirmPassword;
    const hasContactChange = 
      formValues.email !== accountInfo.email || 
      formValues.phoneNumber !== accountInfo.phoneNumber;

    // Validate email format
    if (hasContactChange) {
      if (!formValues.email || !formValues.email.includes('@')) {
        throw new Error('Invalid email address');
      }
      
      // Validate phone format if provided
      if (formValues.phoneNumber && formValues.phoneNumber.trim() !== '') {
        const phoneRegex = /^(\d{3}-\d{3}-\d{4})|(\d{10})$/;
        if (!phoneRegex.test(formValues.phoneNumber)) {
          throw new Error('Invalid phone number format. Use ###-###-#### or ##########');
        }
      }
    }

    // Validate password if provided
    if (hasPasswordChange) {
      if (formValues.password !== formValues.confirmPassword) {
        throw new Error('Passwords must match!');
      }
      updates.push(updatePassword(formValues));
    }

    // Update contact info if changed
    if (hasContactChange) {
      updates.push(updateMyAccount({
        email: formValues.email,
        phoneNumber: formValues.phoneNumber,
      }));
    }

    // If nothing to update
    if (updates.length === 0) {
      throw new Error('No changes to save');
    }

    // Execute all updates
    await Promise.all(updates);
    
    // Update local state with new values
    const updatedInfo = {
      ...formValues,
      password: '',
      confirmPassword: '',
    };
    setAccountInfo(updatedInfo);

    return { message: 'Account updated successfully', model: updatedInfo };
  };

  /**
   * EDIT/CREATE INPUTS
   */
  const myAccountInputs = [
    {
      inputType: 'gridcontainer',
      children: [
        {
          inputType: 'griditem',
          children: [
            {
              inputType: 'text',
              field: 'id',
              label: 'User ID',
              validate: [v.required()],
              disabledOnEdit: true,
            },
          ],
        },
        {
          inputType: 'griditem',
          children: [
            {
              inputType: 'text',
              field: 'userName',
              label: 'Username',
              validate: [v.required()],
              disabledOnEdit: true,
            },
          ],
        },
        {
          inputType: 'griditem',
          children: [
            {
              inputType: 'text',
              field: 'email',
              label: 'Email',
              validate: [v.required(), v.email()],
              autoComplete: 'email',
            },
          ],
        },
        {
          inputType: 'griditem',
          children: [
            {
              inputType: 'text',
              field: 'phoneNumber',
              label: 'Phone Number',
              validate: [v.phone()],
              autoComplete: 'off',
            },
          ],
        },
        {
          inputType: 'griditem',
          children: [
            {
              inputType: 'password',
              field: 'password',
              label: 'Password',
              placeholder: 'Enter a new password here to reset it',
              autoComplete: 'new-password',
            },
          ],
        },
        {
          inputType: 'griditem',
          children: [
            {
              inputType: 'password',
              field: 'confirmPassword',
              label: 'Confirm Password',
              placeholder: 'Confirm new password',
              autoComplete: 'new-password',
            },
          ],
        },
      ],
    },
  ];

  return (
    <div className={classnames('grid-container')}>
      {/* MFA Status Pill */}
      {mfaInfo && (
        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Typography variant="body1">
            <strong>Two-Factor Authentication:</strong>
          </Typography>
          {mfaInfo.twoFactorEnabled ? (
            <Chip label="Enabled" style={{ backgroundColor: '#17c80d', color: '#fff' }} size="small" />
          ) : (
            <Chip label="Disabled" style={{ backgroundColor: '#d32f2f', color: '#fff' }} size="small" />
          )}
        </div>
      )}

      <DynamicForm
        mode={'EDIT'}
        inputs={myAccountInputs}
        data={accountInfo}
        onSave={formValues => handleSave(Object.assign({}, accountInfo, formValues))}
      />

      {/* Trusted Devices Section */}
      {trustedDevices.length > 0 && (
        <div style={{ marginTop: 40 }}>
          <Typography variant="h6" style={{ marginBottom: 15, color: 'var(--jmi-blue)' }}>
            Trusted Devices ({trustedDevices.length})
          </Typography>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f5f5f5' }}>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Device</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Created</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Expires</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {trustedDevices.map((device) => (
                <tr key={device.deviceId || device.DeviceId} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px', textAlign: 'left' }}>
                    {device.label || device.Label || 'Unknown Device'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'left', color: '#666' }}>
                    {new Date(device.createdAt || device.CreatedAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'left', color: '#666' }}>
                    {new Date(device.expiresAt || device.ExpiresAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'left' }}>
                    {(device.isExpired || device.IsExpired) ? (
                      <span style={{ color: '#d32f2f', fontWeight: 600 }}>Expired</span>
                    ) : (
                      <span style={{ color: '#4caf50', fontWeight: 600 }}>Active</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

MyAccount.propTypes = {};

MyAccount.defaultProps = {};
